import { useState, useEffect } from 'react';
import {
  PlusIcon,
  CalendarIcon,
  MapPinIcon,
  ClockIcon,
  TrashIcon,
  PencilIcon
} from '@heroicons/react/24/outline';
import { getTournamentEvents, deleteEvent } from '../../../lib/supabase';
import { useTournament } from '../../TournamentContext';
import MobileEventSheet from '../../components/MobileEventSheet';
import DeleteConfirmationModal from '../../../components/DeleteConfirmationModal';
import { toast } from 'react-hot-toast';

interface EventData {
  id: string;
  title: string;
  date: string;
  time?: string;
  location?: string;
  description?: string;
  type?: string;
}

const typeColors: Record<string, string> = {
  Registration: 'bg-blue-100 text-blue-700',
  Tournament: 'bg-golf-orange/10 text-golf-orange',
  Ceremony: 'bg-amber-100 text-amber-700',
  Social: 'bg-green-100 text-green-700',
};

export default function MobileAdminEvents() {
  const { selectedTournament } = useTournament();
  const tournamentId = selectedTournament?.id || '';
  const [events, setEvents] = useState<EventData[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<EventData | null>(null);
  const [deletingEvent, setDeletingEvent] = useState<EventData | null>(null);

  useEffect(() => { if (tournamentId) fetchEvents(); }, [tournamentId]);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const { data, error } = await getTournamentEvents(tournamentId);
      if (error) throw error;
      const sorted = (data || []).sort((a: EventData, b: EventData) =>
        new Date(a.date).getTime() - new Date(b.date).getTime()
      );
      setEvents(sorted);
    } catch {
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingEvent) return;
    try {
      const { error } = await deleteEvent(deletingEvent.id);
      if (error) throw error;
      toast.success('Event deleted');
      setDeletingEvent(null);
      fetchEvents();
    } catch {
      toast.error('Failed to delete event');
    }
  };

  const now = new Date();
  const upcoming = events.filter(e => new Date(e.date) > now);
  const past = events.filter(e => new Date(e.date) <= now);

  const renderEvent = (event: EventData) => (
    <div key={event.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${typeColors[event.type || ''] || 'bg-gray-100 text-gray-600'}`}>
              {event.type || 'Event'}
            </span>
          </div>
          <p className="font-semibold text-golf-gray text-sm">{event.title}</p>
          <div className="mt-1.5 space-y-1">
            <div className="flex items-center gap-1.5 text-xs text-gray-500">
              <CalendarIcon className="w-3.5 h-3.5" />
              <span>{new Date(event.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</span>
              {event.time && (
                <>
                  <ClockIcon className="w-3.5 h-3.5 ml-1" />
                  <span>{event.time}</span>
                </>
              )}
            </div>
            {event.location && (
              <div className="flex items-center gap-1.5 text-xs text-gray-500">
                <MapPinIcon className="w-3.5 h-3.5" />
                <span className="truncate">{event.location}</span>
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={() => { setEditingEvent(event); setIsModalOpen(true); }}
            className="p-2 rounded-xl text-gray-400 hover:text-golf-orange hover:bg-golf-orange/5 transition-colors"
          >
            <PencilIcon className="w-4 h-4" />
          </button>
          <button
            onClick={() => setDeletingEvent(event)}
            className="p-2 rounded-xl text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
          >
            <TrashIcon className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="px-4 py-4 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-gray-500">{events.length} total events</p>
        </div>
        <button
          onClick={() => { setEditingEvent(null); setIsModalOpen(true); }}
          className="bg-golf-orange text-white rounded-xl px-3 py-2.5 flex items-center gap-1.5 text-sm font-medium"
        >
          <PlusIcon className="w-4 h-4" />
          Add Event
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-golf-orange border-t-transparent" />
        </div>
      ) : events.length === 0 ? (
        <div className="text-center py-16">
          <CalendarIcon className="w-12 h-12 text-gray-200 mx-auto mb-3" />
          <p className="text-gray-400 text-sm">No events scheduled yet</p>
        </div>
      ) : (
        <>
          {upcoming.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2 px-1">Upcoming</p>
              <div className="space-y-2">{upcoming.map(renderEvent)}</div>
            </div>
          )}
          {past.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2 px-1">Past</p>
              <div className="space-y-2 opacity-60">{past.map(renderEvent)}</div>
            </div>
          )}
        </>
      )}

      {tournamentId && (
        <MobileEventSheet
          isOpen={isModalOpen}
          onClose={() => { setIsModalOpen(false); setEditingEvent(null); fetchEvents(); }}
          tournamentId={tournamentId}
          event={editingEvent}
        />
      )}

      <DeleteConfirmationModal
        isOpen={!!deletingEvent}
        onClose={() => setDeletingEvent(null)}
        onConfirm={handleDelete}
        title="Delete Event"
        message={`Are you sure you want to delete "${deletingEvent?.title}"?`}
      />
    </div>
  );
}
